import React, { Children, isValidElement, useMemo } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import styles from "./AnimationGroup.module.scss";
import classNames from "classnames";
import { WSList, WSListProps } from "@wingspanhq/fe-component-library";

export type AnimationType =
  | "slideDown"
  | "slideUp"
  | "slideLeft"
  | "slideRight"
  | "fade"
  | "scale"
  | "bounce";

interface BaseAnimationGroupProps {
  children: React.ReactNode;
  component?: React.ComponentType<any> | string;
  startDelay?: number; // initial delay before animation starts
  type?: AnimationType;
  timeout?: number; // animation duration in ms
  ready?: boolean; // if false, prevents rendering any items (useful when waiting for data)
}

// Generic type that extends base props and allows additional wrapper component props
export type AnimationGroupProps<T extends Record<string, any> = WSListProps> =
  BaseAnimationGroupProps & T;

const getAnimationClassNames = (type: AnimationType) => ({
  enter: styles[`${type}Enter`],
  enterActive: styles[`${type}EnterActive`],
  enterDone: styles[`${type}EnterDone`],
  exit: styles[`${type}Exit`],
  exitActive: styles[`${type}ExitActive`],
  exitDone: styles[`${type}ExitDone`]
});

export function AnimationGroup<T extends Record<string, any> = WSListProps>({
  children,
  component: Component = WSList,
  startDelay = 0,
  type = "slideDown",
  timeout = 300,
  ready = true,
  ...restProps
}: AnimationGroupProps<T>) {
  const childrenArray = useMemo(() => Children.toArray(children), [children]);

  const animatedChildren = childrenArray.filter(Boolean).map((child, index) => {
    const shouldShow =
      isValidElement(child) && "show" in (child.props as any)
        ? (child.props as any).show
        : true;

    if (!shouldShow || !ready) {
      return null;
    }

    const childContent =
      isValidElement(child) && "show" in (child.props as any)
        ? React.cloneElement(child as any, { show: undefined })
        : child;

    const key = (child as any).key || Math.random();

    return (
      <CSSTransition
        key={key}
        timeout={timeout + startDelay}
        classNames={getAnimationClassNames(type)}
      >
        <div
          className={styles[`${type}Animation`]}
          style={{
            transitionDuration: `${timeout}ms`,
            transitionDelay: `${startDelay}ms`
          }}
        >
          {childContent}
        </div>
      </CSSTransition>
    );
  });

  const className = classNames(styles.wrapper, restProps.className);

  return (
    <TransitionGroup component={Component} {...restProps} className={className}>
      {animatedChildren}
    </TransitionGroup>
  );
}
